import { forwardRef, type ReactNode } from 'react';
import { Button as RAButton, type ButtonProps as RAButtonProps } from 'react-aria-components';

import { Loading } from './loading';
import {
  classnames,
  getBackgroundColorClasses,
  getBorderColorClasses,
  getSizeClasses,
  getStateClasses,
  getTextColorClasses,
  type Size,
} from './utils';

type ButtonColor = 'primary' | 'danger' | 'default';

interface Props {
  variant?: 'solid' | 'outlined' | 'link' | 'icon';
  primary?: boolean;
  danger?: boolean;
  size?: Size;
  isLoading?: boolean;
  className?: string;
}

interface Slots {
  icon?: ReactNode;
  children?: ReactNode;
}

export type ButtonProps = Props & Slots & RAButtonProps;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, primary, danger, size, isLoading, icon, children, className, isDisabled, ...raProps },
  ref,
) {
  size = size || 'md';

  const classNames: string[] = [variant === 'icon' ? '' : getSizeClasses(size), getStateClasses()];

  let color: ButtonColor;
  if (danger) {
    color = 'danger';
    variant = variant || 'solid';
  } else if (primary) {
    color = 'primary';
    variant = variant || 'solid';
  } else {
    color = variant === 'link' ? 'primary' : 'default';
  }

  variant = variant || 'outlined';

  classNames.push(getColorClasses(variant, color));

  return (
    <RAButton
      {...raProps}
      ref={ref}
      isDisabled={isDisabled || isLoading}
      type={raProps.type || 'button'}
      className={classnames('inline-flex items-center justify-center outline-none', classNames, className)}
    >
      {children}
      {isLoading && <Loading className={classnames('text-current')} />}
      {!isLoading && icon}
    </RAButton>
  );
});

function getColorClasses(variant: string, color: ButtonColor) {
  switch (variant) {
    case 'solid': {
      return `${getBackgroundColorClasses(color)} ${color === 'default' ? 'text-font-reverse' : 'text-white'}`;
    }
    case 'outlined': {
      return `${getBorderColorClasses(color)} ${getTextColorClasses(color)}`;
    }
    case 'link':
    case 'icon': {
      return getTextColorClasses(color);
    }
    // No default
  }
  return '';
}
